import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import JobTile from "./JobTile";
import {
  FaAngleDown,
  FaAngleUp,
  FaAngleLeft,
  FaAngleRight,
} from "react-icons/fa";
import jobTitles from "../jobs_titles.json";
import Sidebar from "./Sidebar";
import "../styles/components/jobSearch.scss";
import "../styles/components/pagination.scss";
import { fetchMatchedJobs } from "../jobService";
import ReactPaginate from "react-paginate";
import JobDetailsModal, { JobData } from "./JobDetailsModal";
import {
  JobButtonContext,
  normalizeExperienceLevel,
  experienceLevelOptions,
  type JobSubcategory
} from "../constant";
import { AiOutlineClose, AiOutlineFilter } from "react-icons/ai";
import { getApplications } from '../services/applicationService';
import { FiMenu } from 'react-icons/fi';
import companyInfoService from '../services/companyInfo.service';
import axiosClient from "../axios-client";

// Define types for better type safety
interface JobHub {
  value: string;
  label: string;
}

interface ExperienceLevel {
  value: string;
  label: string;
}

interface IndustryTag {
  value: string;
  label: string;
  source: 'company' | 'job';
  parentCategory?: string;
}

interface Job {
  _id: string;
  title: string;
  company: string;
  location: string;
  location_hub?: string;
  jobType?: string;
  experience_level: string;
  description?: string;
  requirements?: string[];
  skills?: string[];
  matchScore?: number;
  industry?: string;
  categories?: string[];
  companyIndustries: string[];
  companyIndustriesNormalized?: string[];
  days_to_live?: any;
  job_url?: string;
  workEnvironment?: string;
  environment?: string;
  [key: string]: any;
}

interface GroupedSubcategories {
  [key: string]: JobSubcategory[];
}

interface JobCache {
  allJobs: Job[];
  timestamp: number;
  filters: {
    locations: string[];
    experienceLevels: string[];
    industries: string[];
  };
  isComplete: boolean;
  totalCount: number;
  
  // Add these properties to make it compatible with FilterIndex
  locationIndex: Map<string, Set<string>>;
  environmentIndex: Map<string, Set<string>>;
  industryIndex: Map<string, Set<string>>;
  jobsById: Map<string, Job>;
  optionCounts: {
    locations: Map<string, number>;
    environments: Map<string, number>;
    industries: Map<string, number>;
  };
}

const defaultPreferences = {
  locations: [], // Empty means "Open to All"
  experienceLevel: ["ENTRY_LEVEL"],
  industries: Object.keys(jobTitles).map((key) => ({ value: key, label: key })),
};

interface FilterDropdownProps {
  options: FilterOption[];
  selectedValues: string[];
  setSelectedValues: (values: string[]) => void;
  title: string;
  type: string;
  loading: boolean;
  activeDropdown: string | null;
  setActiveDropdown: (type: string | null) => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({ 
  options, 
  selectedValues, 
  setSelectedValues, 
  title, 
  type, 
  loading,
  activeDropdown,
  setActiveDropdown
}) => {
  const isOpen = activeDropdown === type;
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (loading) return;
    setActiveDropdown(isOpen ? null : type);
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (!isOpen) return;
      const target = e.target as HTMLElement;
      if (dropdownRef.current && !dropdownRef.current.contains(target)) {
        setActiveDropdown(null);
      }
    };

    // Add wheel event handler to prevent scroll propagation
    const handleDropdownWheel = (e: WheelEvent) => {
      if (!isOpen) return;
      
      const dropdown = dropdownRef.current?.querySelector('.dropdown-content');
      if (!dropdown) return;
      
      const { scrollTop, scrollHeight, clientHeight } = dropdown as HTMLElement;
      const isAtTop = scrollTop === 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      
      // If scrolling up at the top or down at the bottom, prevent propagation
      if ((e.deltaY < 0 && isAtTop) || (e.deltaY > 0 && isAtBottom)) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    
    // Handle touch moves for mobile devices
    const handleTouchMove = (e: TouchEvent) => {
      if (!isOpen) return;
      
      const dropdown = dropdownRef.current?.querySelector('.dropdown-content') as HTMLElement;
      if (!dropdown) return;
      
      // Check if the touch is happening inside our dropdown
      const touch = e.touches[0];
      const touchTarget = document.elementFromPoint(touch.clientX, touch.clientY);
      if (!touchTarget || !dropdown.contains(touchTarget)) return;
      
      const { scrollTop, scrollHeight, clientHeight } = dropdown;
      const isAtTop = scrollTop <= 0;
      const isAtBottom = scrollTop + clientHeight >= scrollHeight - 1;
      
      // Get touch direction (up or down)
      const touchY = touch.clientY;
      const lastTouchY = (dropdown as any)._lastTouchY || touchY;
      const touchDirection = touchY > lastTouchY ? 'down' : 'up';
      (dropdown as any)._lastTouchY = touchY;
      
      // Prevent default only when we're at the boundaries
      if ((touchDirection === 'up' && isAtBottom) || (touchDirection === 'down' && isAtTop)) {
        e.preventDefault();
      }
    };

    document.addEventListener('click', handleClickOutside);
    document.addEventListener('wheel', handleDropdownWheel, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('wheel', handleDropdownWheel);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [isOpen, setActiveDropdown, type]);

  // Group options by parent category for industries
  const isCategory = type === 'industries';

  // Group subcategories by parent category
  const groupedOptions = isCategory ? 
    options.reduce((acc: { [key: string]: FilterOption[] }, option: FilterOption) => {
      // Only group items that have a parentCategory and explicitly exclude 'Company Industries'
      if (option.parentCategory && option.parentCategory !== 'Company Industries') {
        if (!acc[option.parentCategory]) {
          acc[option.parentCategory] = [];
        }
        acc[option.parentCategory].push(option);
      }
      return acc;
    }, {}) : {};
    
  // Get the flat list of options without parentCategory (company industries)
  const flatOptions = isCategory ? 
    options.filter(option => 
      !option.parentCategory || option.parentCategory === 'Company Industries'
    ) : 
    options;

  return (
    <div 
      className={`dropdown ${isOpen ? 'active' : ''}`} 
      data-type={type}
      onClick={(e) => e.stopPropagation()}
      ref={dropdownRef}
    >
      <button
        className="dropdown-button"
        onClick={handleToggle}
        disabled={loading}
        style={{ cursor: loading ? 'not-allowed' : 'pointer' }}
      >
        {title}
        {selectedValues.length > 0 && (
          <span className="selected-count">{selectedValues.length}</span>
        )}
        {isOpen ? <FaAngleUp /> : <FaAngleDown />}
      </button>
      <div className={`dropdown-content ${isOpen ? 'open' : ''}`}>
        {isCategory ? (
          // Render both flat options and hierarchical categories
          <>
            {/* Render flat options first (company industries) */}
            {flatOptions.length > 0 && (
              <div className="flat-options">
                {flatOptions.map((option) => (
                  <div key={option.value} className="checkbox-item">
                    <div className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selectedValues.includes(option.value)}
                        onChange={() => {
                          const newValues = selectedValues.includes(option.value)
                            ? selectedValues.filter((v) => v !== option.value)
                            : [...selectedValues, option.value];
                          setSelectedValues(newValues);
                        }}
                        id={`${type}-${option.value}`}
                      />
                      <label htmlFor={`${type}-${option.value}`}>
                        {option.label} {option.count > 0 && <span className="option-count">({option.count})</span>}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add separator if we have both flat options and hierarchical categories */}
            {flatOptions.length > 0 && Object.keys(groupedOptions).length > 0 && (
              <div className="dropdown-separator"></div>
            )}
            
            {/* Render hierarchical categories */}
            {Object.entries(groupedOptions).map(([categoryName, subcategories]) => (
              <div key={categoryName} className="category-group">
                <div className="checkbox-item category-parent">
                  <div className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={subcategories.every(sub => selectedValues.includes(sub.value))}
                      ref={input => {
                        if (input) {
                          input.indeterminate = subcategories.some(sub => selectedValues.includes(sub.value)) &&
                            !subcategories.every(sub => selectedValues.includes(sub.value));
                        }
                      }}
                      onChange={() => {
                        const subValues = subcategories.map(sub => sub.value);
                        const allSelected = subcategories.every(sub => selectedValues.includes(sub.value));
                        if (allSelected) {
                          setSelectedValues(selectedValues.filter(v => !subValues.includes(v)));
                        } else {
                          const newValues = [...selectedValues];
                          subValues.forEach(value => {
                            if (!newValues.includes(value)) {
                              newValues.push(value);
                            }
                          });
                          setSelectedValues(newValues);
                        }
                      }}
                      id={`${type}-${categoryName}`}
                    />
                    <label htmlFor={`${type}-${categoryName}`}>{categoryName}</label>
                  </div>
                </div>
                <div className="subcategories">
                  {subcategories.map((subcategory) => (
                    <div key={subcategory.value} className="checkbox-item subcategory">
                      <div className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={selectedValues.includes(subcategory.value)}
                          onChange={() => {
                            const newValues = selectedValues.includes(subcategory.value)
                              ? selectedValues.filter((v) => v !== subcategory.value)
                              : [...selectedValues, subcategory.value];
                            setSelectedValues(newValues);
                          }}
                          id={`${type}-${subcategory.value}`}
                        />
                        <label htmlFor={`${type}-${subcategory.value}`}>
                          {subcategory.label} {subcategory.count > 0 && <span className="option-count">({subcategory.count})</span>}
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        ) : (
          // Render regular options using the same pattern as flat options for consistency
          <div className="flat-options">
            {options.map((option) => (
              <div key={option.value} className="checkbox-item">
                <div className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(option.value)}
                    onChange={() => {
                      const newValues = selectedValues.includes(option.value)
                        ? selectedValues.filter((v) => v !== option.value)
                        : [...selectedValues, option.value];
                      setSelectedValues(newValues);
                    }}
                    id={`${type}-${option.value}`}
                  />
                  <label htmlFor={`${type}-${option.value}`}>
                    {option.label} {option.count > 0 && <span className="option-count">({option.count})</span>}
                  </label>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Add new interfaces for our advanced filtering system
interface FilterState {
  locations: string[];
  experienceLevels: string[];
  industries: string[];
}

interface FilterOption {
  value: string;
  label: string;
  count: number; // Number of jobs matching this option with current other filters
  source?: 'company' | 'job';
  parentCategory?: string;
}

interface FilterIndex {
  // Primary data
  allJobs: Job[];
  
  // Indexes for each filter dimension
  locationIndex: Map<string, Set<string>>; // Map of location to job IDs
  environmentIndex: Map<string, Set<string>>; // Map of environment to job IDs
  industryIndex: Map<string, Set<string>>; // Map of industry to job IDs
  
  // Reverse lookup for getting jobs by ID
  jobsById: Map<string, Job>;
  
  // Count cache
  optionCounts: {
    locations: Map<string, number>;
    environments: Map<string, number>;
    industries: Map<string, number>;
  };
}

const JobSearch: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [selectedEnvironments, setSelectedEnvironments] = useState<string[]>([]);
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [jobResults, setJobResults] = useState<Job[]>([]);
  const [error, setError] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const dropdownRefs = useRef<{ [key: string]: any }>({});
  const navigate = useNavigate();
  const [user, setUser] = useState<any>();
  const [selection, setSelection] = useState<JobData | null>(null);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const itemsPerPage = 54;
  const [availableJobHubs, setAvailableJobHubs] = useState<JobHub[]>([]);
  const [availableExperienceLevels, setAvailableExperienceLevels] = useState<ExperienceLevel[]>(experienceLevelOptions);
  const [loading, setLoading] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isModalClosing, setIsModalClosing] = useState(false);
  const [tempSelectedLocations, setTempSelectedLocations] = useState<string[]>([]);
  const [tempSelectedEnvironments, setTempSelectedEnvironments] = useState<string[]>([]);
  const [tempSelectedIndustries, setTempSelectedIndustries] = useState<string[]>([]);
  const [originalMatchedJobs, setOriginalMatchedJobs] = useState<Job[]>([]);
  const [appliedJobs, setAppliedJobs] = useState<Set<string>>(new Set());
  const [fullCategorySet, setFullCategorySet] = useState<Set<string>>(new Set());
  const [dropdownOpen, setDropdownOpen] = useState<{ [key: string]: boolean }>({});
  const [preferencesSaved, setPreferencesSaved] = useState(false);
  const [isClearing, setIsClearing] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [jobCache, setJobCache] = useState<JobCache | null>(null);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isFilteringLocally, setIsFilteringLocally] = useState(false);
  
  // Add a ref to track actual page state completely disconnected from React Router
  const pageStateRef = useRef({
    currentPage: 1,
    isChangingPage: false,
    totalPages: 1
  });

  // Add this ref at the top level of the component
  const prevFiltersRef = useRef({
    locations: [] as string[],
    experienceLevels: [] as string[],
    industries: [] as string[]
  });

  // Add a useEffect to initialize filter references
  useEffect(() => {
    // Initialize the ref with current filter values when component mounts
    prevFiltersRef.current = {
      locations: selectedLocations,
      experienceLevels: selectedEnvironments,
      industries: selectedIndustries
    };
  }, [selectedLocations, selectedEnvironments, selectedIndustries]);

  // Separate URL page state from data fetching
  const [currentPageData, setCurrentPageData] = useState<{
    page: number;
    jobs: Job[];
  }>({
    page: parseInt(searchParams.get("page") || "1", 10),
    jobs: []
  });

  // Add new state for company industries
  const [availableCompanyIndustries, setAvailableCompanyIndustries] = useState<IndustryTag[]>([]);

  // Add ref for tracking initial fetch
  const initialFetchDone = useRef(false);
  const prevDependencies = useRef({
    selectedLocations,
    selectedEnvironments,
    selectedIndustries
  });

  // Clear company industries cache on component mount
  useEffect(() => {
    // This ensures we always start with a fresh company industry list
    setAvailableCompanyIndustries([]);
  }, []);

  // Add a reset function to completely clear industry data
  const resetIndustriesData = useCallback(() => {
    setAvailableCompanyIndustries([]); // Start with empty company industries
    
    // This will force a re-fetch of all data
    setJobCache(null);
  }, []);

  // Use the reset function when location or experience filters change
  useEffect(() => {
    // Reset industry data whenever location or experience filters change
    resetIndustriesData();
  }, [selectedLocations, selectedEnvironments, resetIndustriesData]);

  // Define a processing key storage using useMemo to prevent constant recreation
  const processingKeys = useMemo(() => new Set<string>(), []);

  // Add our new filter index
  const [filterIndex, setFilterIndex] = useState<FilterIndex | null>(null);
  
  // Add state for available options with counts
  const [availableLocationOptions, setAvailableLocationOptions] = useState<FilterOption[]>([]);
  const [availableExperienceLevelOptions, setAvailableExperienceLevelOptions] = useState<FilterOption[]>([]);
  const [availableIndustryOptions, setAvailableIndustryOptions] = useState<FilterOption[]>([]);

  // Add a function to build the filter index from jobs
  const buildFilterIndex = useCallback((jobs: Job[]): FilterIndex => {
    const locationIndex = new Map<string, Set<string>>();
    const environmentIndex = new Map<string, Set<string>>();
    const industryIndex = new Map<string, Set<string>>();
    const jobsById = new Map<string, Job>();
    
    // Build the indexes
    jobs.forEach(job => {
      // Store job by ID for reverse lookup
      jobsById.set(job._id, job);
      
      // Index by location
      if (job.location_hub) {
        const locations = job.location_hub.split(';').map(loc => 
          loc.trim().toLowerCase().replace(/\s+/g, '_')
        );
        
        locations.forEach(location => {
          if (!locationIndex.has(location)) {
            locationIndex.set(location, new Set());
          }
          locationIndex.get(location)?.add(job._id);
        });
      }
      
      // Index by environment
      const envRaw = (job.workEnvironment || job.environment || job.work_environment || '').trim();
      if (envRaw) {
        const envValue = envRaw.toLowerCase().replace(/\s+/g, '_');
        if (!environmentIndex.has(envValue)) environmentIndex.set(envValue, new Set());
        environmentIndex.get(envValue)?.add(job._id);
      }
      
      // Index by industry
      if (job.companyIndustriesNormalized && job.companyIndustriesNormalized.length > 0) {
        job.companyIndustriesNormalized.forEach(industry => {
          if (!industryIndex.has(industry)) {
            industryIndex.set(industry, new Set());
          }
          industryIndex.get(industry)?.add(job._id);
        });
      }
    });
    
    // Create empty counts
    const optionCounts = {
      locations: new Map<string, number>(),
      environments: new Map<string, number>(),
      industries: new Map<string, number>()
    };
    
    // Return the complete index
    return {
      allJobs: jobs,
      locationIndex,
      environmentIndex,
      industryIndex,
      jobsById,
      optionCounts
    };
  }, []);
  
  // Fix the calculateValidOptions function that was accidentally removed
  const calculateValidOptions = useCallback((index: FilterIndex, currentFilters: FilterState) => {
    // Step 1: Get sets of job IDs that match each filter type
    let matchingJobIds: Set<string> | null = null;
    
    // Match ALL selected locations (OR logic between locations)
    if (currentFilters.locations.length > 0) {
      const locationJobIds = new Set<string>();
      
      currentFilters.locations.forEach(location => {
        const jobIds = index.locationIndex.get(location);
        if (jobIds) {
          jobIds.forEach(id => locationJobIds.add(id));
        }
      });
      
      matchingJobIds = locationJobIds;
    }
    
    // Match ANY selected environment (OR logic between environments)
    if (currentFilters.experienceLevels.length > 0) {
      const envJobIds = new Set<string>();
      
      currentFilters.experienceLevels.forEach(env => {
        const jobIds = index.environmentIndex.get(env);
        if (jobIds) {
          jobIds.forEach(id => envJobIds.add(id));
        }
      });
      
      // Intersect with previous results if any (AND logic between different filter types)
      if (matchingJobIds === null) {
        matchingJobIds = envJobIds;
      } else {
        matchingJobIds = new Set(Array.from(matchingJobIds).filter(id => envJobIds.has(id)));
      }
    }
    
    // Match ANY selected industry (OR logic between industries)
    if (currentFilters.industries.length > 0) {
      const industryJobIds = new Set<string>();
      
      currentFilters.industries.forEach(industry => {
        const jobIds = index.industryIndex.get(industry);
        if (jobIds) {
          jobIds.forEach(id => industryJobIds.add(id));
        }
      });
      
      // Intersect with previous results if any (AND logic between different filter types)
      if (matchingJobIds === null) {
        matchingJobIds = industryJobIds;
      } else {
        matchingJobIds = new Set(Array.from(matchingJobIds).filter(id => industryJobIds.has(id)));
      }
    }
    
    // If no filters are selected, all jobs match
    if (matchingJobIds === null) {
      matchingJobIds = new Set(index.jobsById.keys());
    }
    
    // Step 2: Calculate valid options for each filter dimension
    // We'll count how many jobs would match if you selected each option
    
    // For locations
    const validLocationOptions: FilterOption[] = [];
    const locationCounts = new Map<string, number>();
    
    // Process each unique location
    index.locationIndex.forEach((jobIds, location) => {
      // Count how many jobs match this location AND other selected filters
      const count = Array.from(jobIds).filter(id => matchingJobIds!.has(id)).length;
      
      // Only include locations with at least one job
      if (count > 0) {
        // Store the count for this location
        locationCounts.set(location, count);
        
        // Format the location label for display
        let formattedLabel = '';
        
        // Handle special case for D.C. and other abbreviations
        if (location.includes('.')) {
          formattedLabel = location.split('_').map(word => {
            if (word.includes('.')) {
              // Keep abbreviations in uppercase
              return word.split('.').map(part => part.toUpperCase()).join('.');
            } else {
              // Normal word capitalization
              return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
            }
          }).join(' ');
        } else {
          // Standard title case formatting
          formattedLabel = location.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          ).join(' ');
        }
        
        // Add to our options
        validLocationOptions.push({
          value: location,
          label: formattedLabel,
          count
        });
      }
    });
    
    // Build label map for environments from original job data
    const environmentLabels = new Map<string, string>();
    index.allJobs.forEach(job => {
      const rawEnv = (job.workEnvironment || job.environment || job.work_environment || '').trim();
      if (rawEnv) {
        const key = rawEnv.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
        environmentLabels.set(key, rawEnv);
      }
    });

    // For experience levels (we store Work Environment values here)
    const validExperienceLevelOptions: FilterOption[] = [];
    const experienceCounts = new Map<string, number>();
    
    // Process each experience level
    index.environmentIndex.forEach((jobIds, env) => {
      // Count how many jobs match this level AND other selected filters
      const count = Array.from(jobIds).filter(id => matchingJobIds!.has(id)).length;
      
      // Only include levels with at least one job
      if (count > 0) {
        // Store the count
        experienceCounts.set(env, count);
        
        // Find the label from our predefined options
        const labelFromMap = environmentLabels.get(env);
        // Title-case fallback (handle hyphens)
        const fallbackLabel = env.split(/[_-]/).map(word => word.charAt(0).toUpperCase() + word.slice(1)).join('-');
        validExperienceLevelOptions.push({
          value: env,
          label: labelFromMap || fallbackLabel,
          count
        });
      }
    });
    
    // For industries
    const validIndustryOptions: FilterOption[] = [];
    const industryCounts = new Map<string, number>();
    
    // First collect unique industries with their labels from jobs
    const industryLabels = new Map<string, string>();
    index.allJobs.forEach(job => {
      if (job.companyIndustries && job.companyIndustriesNormalized) {
        for (let i = 0; i < job.companyIndustriesNormalized.length; i++) {
          const value = job.companyIndustriesNormalized[i];
          const label = job.companyIndustries[i];
          if (value && label) {
            industryLabels.set(value, label);
          }
        }
      }
    });
    
    // Process each industry
    index.industryIndex.forEach((jobIds, industry) => {
      // Count how many jobs match this industry AND other selected filters
      const count = Array.from(jobIds).filter(id => matchingJobIds!.has(id)).length;
      
      // Only include industries with at least one job
      if (count > 0) {
        // Store the count
        industryCounts.set(industry, count);
        
        // Add to our options
        validIndustryOptions.push({
          value: industry,
          label: industryLabels.get(industry) || industry,
          count,
          source: 'company'
        });
      }
    });
    
    // Sort options alphabetically by label
    validLocationOptions.sort((a, b) => a.label.localeCompare(b.label));
    validExperienceLevelOptions.sort((a, b) => a.label.localeCompare(b.label));
    validIndustryOptions.sort((a, b) => a.label.localeCompare(b.label));
    
    // Update the count cache in the index
    const updatedIndex = { 
      ...index,
      optionCounts: {
        locations: locationCounts,
        environments: experienceCounts,
        industries: industryCounts
      }
    };
    
    // Return everything
    return {
      locationOptions: validLocationOptions,
      experienceLevelOptions: validExperienceLevelOptions, 
      industryOptions: validIndustryOptions,
      matchingJobIds: Array.from(matchingJobIds),
      updatedIndex
    };
  }, []);

  // Replace the collectCompanyIndustries function with our improved version
  const collectCompanyIndustries = useCallback((jobs: Job[], updateState = true) => {
    if (!jobs || jobs.length === 0) return { industriesMap: {}, tags: [], enhancedJobs: [] };
    
    // Extract unique company names
    const companyNames = Array.from(new Set(jobs.map(job => job.company || '').filter(Boolean)));
    
    // Get industries for all companies at once
    const industriesMap = companyInfoService.getCompanyIndustriesBatch(companyNames);
    
    // Process the industries to create tags and update jobs with normalized industries
    const uniqueIndustries = new Map<string, string>();
    
    // Process each company and its industries
    Object.entries(industriesMap).forEach(([company, industries]) => {
      if (industries && industries.length > 0) {
        // Add each industry to our Map
        industries.forEach(industry => {
          if (industry && industry.trim() !== '') {
            const normalizedValue = industry.toLowerCase().replace(/[^a-z0-9]+/g, '_');
            uniqueIndustries.set(normalizedValue, industry.trim());
          }
        });
      }
    });
    
    // Convert Map to array for display - these are ONLY the company industries
    const tags = Array.from(uniqueIndustries.entries())
      .map(([value, label]) => ({
        value,
        label,
        source: 'company' as const,
        count: 0 // We'll calculate this with our index
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    
    // Update jobs with their industries
    const enhancedJobs = jobs.map(job => {
      const companyIndustries = job.company ? (industriesMap[job.company] || []) : [];
      const companyIndustriesNormalized = companyIndustries.map(industry => 
        industry.toLowerCase().replace(/[^a-z0-9]+/g, '_')
      );
      
      return {
        ...job,
        companyIndustries,
        companyIndustriesNormalized
      };
    });
    
    // Only update state if requested - helps prevent infinite loops
    if (updateState) {
      // Build our optimized index
      const index = buildFilterIndex(enhancedJobs);
      setFilterIndex(index);
      
      // Calculate initial valid options with no filters selected
      const { 
        locationOptions, 
        experienceLevelOptions, 
        industryOptions,
        updatedIndex
      } = calculateValidOptions(index, {
        locations: [],
        experienceLevels: [],
        industries: []
      });
      
      // Update our state with the calculated options
      setAvailableLocationOptions(locationOptions);
      setAvailableExperienceLevelOptions(experienceLevelOptions);
      setAvailableIndustryOptions(industryOptions);
      
      // Update the displayed results based on current page
      const page = parseInt(searchParams.get("page") || "1", 10);
      const startIndex = (page - 1) * itemsPerPage;
      const endIndex = startIndex + itemsPerPage;
      setJobResults(enhancedJobs.slice(startIndex, endIndex));
      setOriginalMatchedJobs(enhancedJobs);
    }
    
    return {
      industriesMap,
      tags,
      enhancedJobs
    };
  }, [searchParams, itemsPerPage, buildFilterIndex, calculateValidOptions]);

  // Replace the filterJobsFromCache function with our improved version
  const filterJobsFromCache = useCallback((index: FilterIndex, filters: FilterState) => {
    if (!index || !index.allJobs || index.allJobs.length === 0) {
      return [];
    }
    
    // Calculate valid options and get matching job IDs
    const { matchingJobIds } = calculateValidOptions(index, filters);
    
    // Convert job IDs to actual job objects
    const filteredJobs = matchingJobIds.map(id => index.jobsById.get(id)!).filter(Boolean);
    
    return filteredJobs;
  }, [calculateValidOptions]);

  // Update the fetchAndFilterJobs function to use our optimized system
  const fetchAndFilterJobs = useCallback(async (forceRefresh = false) => {
    // Create a processing key based on current filters
    const processingKey = JSON.stringify({ 
      locations: selectedLocations, 
      experienceLevels: selectedEnvironments, 
      industries: selectedIndustries,
      forceRefresh
    });
    
    // Check if we're already processing this exact request
    if (processingKeys.has(processingKey)) {
      return;
    }
    
    processingKeys.add(processingKey);
    
    try {
      // CRITICAL: Save current page state before API call
      const savedPageState = { ...pageStateRef.current };
      
      // Get the current page from URL
      const currentPage = pageStateRef.current.currentPage;
      
      // Check if we can filter from index instead of making an API call
      if (!forceRefresh && filterIndex && filterIndex.allJobs.length > 0) {
        setIsFilteringLocally(true);
        
        // Filter using our optimized index
        const filteredJobs = filterJobsFromCache(filterIndex, {
          locations: selectedLocations,
          experienceLevels: selectedEnvironments,
          industries: selectedIndustries
        });
        
        // Recalculate valid options based on current filters
        const { 
          locationOptions, 
          experienceLevelOptions, 
          industryOptions,
          updatedIndex
        } = calculateValidOptions(filterIndex, {
          locations: selectedLocations,
          experienceLevels: selectedEnvironments,
          industries: selectedIndustries
        });
        
        // Update our filter options with counts
        setAvailableLocationOptions(locationOptions);
        setAvailableExperienceLevelOptions(experienceLevelOptions);
        setAvailableIndustryOptions(industryOptions);
        
        // Update the filter index
        setFilterIndex(updatedIndex);
        
        // CRITICAL: Restore page state if we're changing pages, otherwise use page 1
        if (savedPageState.isChangingPage) {
          pageStateRef.current = savedPageState;
        } else {
          pageStateRef.current.currentPage = 1;
        }
        
        // Get the page slice for display
        const startIndex = (pageStateRef.current.currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageJobs = filteredJobs.slice(startIndex, endIndex);
        
        // Update the displayed jobs
        setJobResults(pageJobs);
        setOriginalMatchedJobs(filteredJobs);
        setCurrentPageData({
          page: pageStateRef.current.currentPage,
          jobs: pageJobs
        });
        
        // Update pagination info
        const totalJobCount = filteredJobs.length;
        setTotalResults(totalJobCount);
        setTotalPages(Math.ceil(totalJobCount / itemsPerPage));
        
        setIsFilteringLocally(false);
        setIsInitialLoad(false);
        
        // We're done with cache-based filtering
        processingKeys.delete(processingKey);
        return;
      }
      
      // If we need to make an API call, now we show loading state
      setLoading(true);
      
      // Clear all job results immediately to show loading state
      setJobResults([]);
      setOriginalMatchedJobs([]);
      
      // Generate a unique request ID to bypass API caching
      // Without modifying the API contract
      const uniqueRequestId = Date.now().toString();
      const experienceLevelWithSalt = selectedEnvironments.length > 0 
        ? `${selectedEnvironments.join(',')}_${uniqueRequestId.slice(-4)}` 
        : '';
      
      // Request a large limit to get as many jobs as possible for cache
      // We'll handle pagination client-side
      const response = await fetchMatchedJobs({
        page: currentPage,
        limit: 1000, // Request a large number of jobs to cache
        locations: selectedLocations.length > 0 ? selectedLocations : ['open_to_all'], // Default to 'open_to_all' if no locations selected
        jobTypes: [],
        experienceLevel: experienceLevelWithSalt,
        industries: selectedIndustries
      });
      
      // Process the job data
      let jobData: Job[] = [];
      let totalCount = 0;
      
      if (Array.isArray(response)) {
        jobData = (response as any[]).map(job => ({
          ...job,
          experience_level: job.experience_level || '',
          companyIndustries: job.companyIndustries || []
        })) as Job[];
        totalCount = jobData.length;
      } else if (response.jobs) {
        jobData = (response.jobs as any[]).map(job => ({
          ...job,
          experience_level: job.experience_level || '',
          companyIndustries: job.companyIndustries || []
        })) as Job[];
        totalCount = response.totalJobs || jobData.length;
      }
      
      // Process all data at once to minimize re-renders
      const result = collectCompanyIndustries(jobData, false);
      
      // Build our optimized index from the enhanced jobs
      if (result.enhancedJobs && result.enhancedJobs.length > 0) {
        const newIndex = buildFilterIndex(result.enhancedJobs);
        
        // Calculate valid options for the current filters
        const { 
          locationOptions, 
          experienceLevelOptions, 
          industryOptions,
          matchingJobIds,
          updatedIndex
        } = calculateValidOptions(newIndex, {
          locations: selectedLocations,
          experienceLevels: selectedEnvironments,
          industries: selectedIndustries
        });
        
        // Update our filter options with counts
        setAvailableLocationOptions(locationOptions);
        setAvailableExperienceLevelOptions(experienceLevelOptions);
        setAvailableIndustryOptions(industryOptions);
        
        // Update the filter index
        setFilterIndex(updatedIndex);
        
        // Create a complete JobCache with FilterIndex properties
        const compatibleCache: JobCache = {
          allJobs: result.enhancedJobs,
          timestamp: Date.now(),
          filters: {
            locations: selectedLocations,
            experienceLevels: selectedEnvironments,
            industries: selectedIndustries
          },
          isComplete: true,
          totalCount,
          
          // Add FilterIndex properties
          locationIndex: updatedIndex.locationIndex,
          environmentIndex: updatedIndex.environmentIndex,
          industryIndex: updatedIndex.industryIndex,
          jobsById: updatedIndex.jobsById,
          optionCounts: updatedIndex.optionCounts
        };
        
        // Update the cache
        setJobCache(compatibleCache);
        
        // CRITICAL: Restore page state if we're changing pages, otherwise use page 1
        if (savedPageState.isChangingPage) {
          pageStateRef.current = savedPageState;
        } else {
          pageStateRef.current.currentPage = 1;
        }
        
        // Get jobs for the current page
        const filteredJobs = matchingJobIds.map(id => newIndex.jobsById.get(id)!).filter(Boolean);
        const startIndex = (pageStateRef.current.currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageJobs = filteredJobs.slice(startIndex, endIndex);
        
        // Update the displayed jobs
        setJobResults(pageJobs);
        setOriginalMatchedJobs(filteredJobs);
        setCurrentPageData({
          page: pageStateRef.current.currentPage,
          jobs: pageJobs
        });
        
        // Update pagination info
        setTotalResults(filteredJobs.length);
        setTotalPages(Math.ceil(filteredJobs.length / itemsPerPage));
      }
      
      // Done loading
      setLoading(false);
      setIsInitialLoad(false);
      
    } catch (error) {
      console.error("Error fetching jobs:", error);
      setLoading(false);
      setIsInitialLoad(false);
    } finally {
      // Remove the processing key when done
      processingKeys.delete(processingKey);
    }
  }, [
    selectedLocations, 
    selectedEnvironments, 
    selectedIndustries, 
    collectCompanyIndustries, 
    itemsPerPage, 
    searchParams, 
    processingKeys, 
    jobCache,
    filterJobsFromCache,
    filterIndex,
    calculateValidOptions,
    buildFilterIndex
  ]);

  // Update useEffect to depend on the stable fetchAndFilterJobs function
  useEffect(() => {
    // Only fetch on initial mount
    if (!initialFetchDone.current) {
      initialFetchDone.current = true;
      fetchAndFilterJobs(false);
    }
  }, [fetchAndFilterJobs]);

  // Helper that updates the ?page param only when we are NOT viewing a modal
  const startedWithModal = window.location.pathname.includes('/job/');
  const isFirstRender = useRef(true);

  const safeReplacePageParam = (page: number) => {
    // Skip during first render when we landed directly on modal URL
    if (isFirstRender.current && startedWithModal) return;
    if (window.location.pathname.includes('/job/')) return;
    const url = new URL(window.location.href);
    url.searchParams.set('page', page.toString());
    window.history.replaceState({ page }, '', url.toString());
  };

  const shouldManipulateUrl = !window.location.pathname.includes('/job/');

  // Mark that initial render is done
  useEffect(() => {
    isFirstRender.current = false;
  }, []);

  // Update effect for URL page change 
  useEffect(() => {
    // Skip if we're in the middle of a page change operation
    if (pageStateRef.current.isChangingPage) {
      return;
    }
    
    // Get page from URL
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    
    // If URL page doesn't match our internal state, update our internal state
    if (urlPage !== pageStateRef.current.currentPage) {
      pageStateRef.current.currentPage = urlPage;
      
      // If we have data cached, update the displayed jobs
      if (filterIndex && filterIndex.allJobs.length > 0) {
        const filteredJobs = filterJobsFromCache(filterIndex, {
          locations: selectedLocations,
          experienceLevels: selectedEnvironments,
          industries: selectedIndustries
        });
        
        const startIndex = (urlPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageJobs = filteredJobs.slice(startIndex, endIndex);
        
        setJobResults(pageJobs);
      }
    }
  }, [searchParams, filterIndex, filterJobsFromCache, selectedLocations, selectedEnvironments, selectedIndustries, itemsPerPage]);

  // Add a missing scroll effect that was removed during refactoring
  useEffect(() => {
    const handleScroll = () => {
      // Set scrolled state when scrolled just 30px instead of 50px to make it appear sooner
      setScrolled(window.scrollY > 30);
    };
    
    // Add passive: true for better scroll performance
    window.addEventListener("scroll", handleScroll, { passive: true });
    
    // Initial check in case page is already scrolled on load
    handleScroll();
    
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Update clearFilters function
  const clearFilters = async () => {
    setIsClearing(true);
    
    // Reset selected filters
    setSelectedLocations([]);
    setSelectedEnvironments([]);
    setSelectedIndustries([]);
    
    // Reset to page 1 in our ref
    pageStateRef.current.currentPage = 1;
    pageStateRef.current.isChangingPage = true;
    
    // Update URL directly with DOM manipulation
    safeReplacePageParam(1);
    
    // If we have an index, use it for instant clearing
    if (filterIndex && filterIndex.allJobs.length > 0) {
      // Calculate options with no filters
      const { 
        locationOptions, 
        experienceLevelOptions, 
        industryOptions,
        updatedIndex
      } = calculateValidOptions(filterIndex, {
        locations: [],
        experienceLevels: [],
        industries: []
      });
      
      // Update options
      setAvailableLocationOptions(locationOptions);
      setAvailableExperienceLevelOptions(experienceLevelOptions);
      setAvailableIndustryOptions(industryOptions);
      
      // Update index
      setFilterIndex(updatedIndex);
      
      // All jobs are now the filtered jobs (no filters)
      const allJobs = filterIndex.allJobs;
      
      // Get the first page of jobs
      const pageJobs = allJobs.slice(0, itemsPerPage);
      
      // Update displayed jobs
      setJobResults(pageJobs);
      setOriginalMatchedJobs(allJobs);
      setCurrentPageData({
        page: 1,
        jobs: pageJobs
      });
      
      // Update pagination
      setTotalResults(allJobs.length);
      setTotalPages(Math.ceil(allJobs.length / itemsPerPage));
      pageStateRef.current.totalPages = Math.ceil(allJobs.length / itemsPerPage);
      
      // Update cache filters
      if (jobCache) {
        const updatedCache: JobCache = {
          ...jobCache,
          filters: {
            locations: [],
            experienceLevels: [],
            industries: []
          },
          // Ensure the FilterIndex properties are updated too
          locationIndex: updatedIndex.locationIndex,
          environmentIndex: updatedIndex.environmentIndex,
          industryIndex: updatedIndex.industryIndex,
          jobsById: updatedIndex.jobsById,
          optionCounts: updatedIndex.optionCounts
        };
        setJobCache(updatedCache);
      }
      
      setIsClearing(false);
      
      // Reset URL ignore flag after a delay
      setTimeout(() => {
        pageStateRef.current.isChangingPage = false;
      }, 100);
    } else {
      // If no index, fetch all jobs
      setLoading(true);
      
      try {
        // Force a refresh to get all jobs without filters
        await fetchAndFilterJobs(true);
      } catch (error) {
        console.error('Error clearing filters:', error);
        setError('Failed to clear filters. Please try again.');
      } finally {
        setLoading(false);
        setIsClearing(false);
        
        // Reset URL ignore flag
        setTimeout(() => {
          pageStateRef.current.isChangingPage = false;
        }, 100);
      }
    }
  };

  // Add effect to clear the cache when component unmounts
  useEffect(() => {
    return () => {
      // Clean up memory when component unmounts
      setJobCache(null);
    };
  }, []);

  // Update the state display helpers
  const getLoadingStateText = () => {
    if (loading) return "Loading jobs...";
    if (isFilteringLocally) return "Filtering jobs...";
    return null;
  };

  // Replace the removed currentJobs constant
  const currentJobs = jobResults;

  const handleSelection = useCallback((job: JobData) => {
    // Ensure job_url is always a string, never undefined
    const jobWithUrl = {
      ...job,
      job_url: job.job_url || "" // Use empty string as fallback instead of undefined
    };
    
    setSelection(jobWithUrl);
  }, []);

  // Add a type utility for making Sets work with the spread operator
  function setToArray<T>(set: Set<T>): T[] {
    return Array.from(set);
  }

  // Update the handleOpenFilterModal function to properly set up the filter modal with counts
  const handleOpenFilterModal = () => {
    // Set the temporary filter values to the current selections
    setTempSelectedLocations([...selectedLocations]);
    setTempSelectedEnvironments([...selectedEnvironments]);
    setTempSelectedIndustries([...selectedIndustries]);
    
    // Show the modal
    setIsFilterModalOpen(true);
    setIsModalClosing(false);
    document.body.style.overflow = "hidden";
  };

  // Add back the handleCloseFilterModal function
  const handleCloseFilterModal = () => {
    setIsModalClosing(true);
    setTimeout(() => {
      setIsFilterModalOpen(false);
      setIsModalClosing(false);
      document.body.style.overflow = "unset";
    }, 300);
  };

  // Update the handleApplyFilters function
  const handleApplyFilters = () => {
    // Update the real filters with the temporary selections
    setSelectedLocations(tempSelectedLocations);
    setSelectedEnvironments(tempSelectedEnvironments);
    setSelectedIndustries(tempSelectedIndustries);
    
    // Reset to page 1 in our ref
    pageStateRef.current.currentPage = 1;
    pageStateRef.current.isChangingPage = true;
    
    // Update URL directly with DOM manipulation
    safeReplacePageParam(1);
    
    // If we have a filter index, calculate the new valid options immediately
    if (filterIndex) {
      // This will make the filter change feel instant
      const { 
        locationOptions, 
        experienceLevelOptions, 
        industryOptions,
        updatedIndex
      } = calculateValidOptions(filterIndex, {
        locations: tempSelectedLocations,
        experienceLevels: tempSelectedEnvironments,
        industries: tempSelectedIndustries
      });
      
      // Update the options with counts
      setAvailableLocationOptions(locationOptions);
      setAvailableExperienceLevelOptions(experienceLevelOptions);
      setAvailableIndustryOptions(industryOptions);
      
      // Update the filter index
      setFilterIndex(updatedIndex);
      
      // If we have a job cache, update it too
      if (jobCache) {
        const updatedCache: JobCache = {
          ...jobCache,
          filters: {
            locations: tempSelectedLocations,
            experienceLevels: tempSelectedEnvironments,
            industries: tempSelectedIndustries
          },
          // Ensure FilterIndex properties are updated 
          locationIndex: updatedIndex.locationIndex,
          environmentIndex: updatedIndex.environmentIndex,
          industryIndex: updatedIndex.industryIndex,
          jobsById: updatedIndex.jobsById,
          optionCounts: updatedIndex.optionCounts
        };
        
        setJobCache(updatedCache);
      }
    }
    
    // Close the modal
    handleCloseFilterModal();
    
    // Reset URL ignore flag after a delay
    setTimeout(() => {
      pageStateRef.current.isChangingPage = false;
    }, 100);
  };

  // Add initial fetch for applied jobs
  useEffect(() => {
    const fetchInitialAppliedJobs = async () => {
      try {
        const applications = await getApplications();
        const appliedJobIds = new Set(applications.map(app => app.jobId));
        setAppliedJobs(appliedJobIds);
      } catch (error) {
        console.error('Error fetching initial applied jobs:', error);
      }
    };

    fetchInitialAppliedJobs();
  }, []); // Run once on component mount

  // Add a function to refresh applied jobs
  const refreshAppliedJobs = async () => {
    try {
      const applications = await getApplications();
      const appliedJobIds = new Set(applications.map(app => app.jobId));
      setAppliedJobs(appliedJobIds);
    } catch (error) {
      console.error('Error fetching applied jobs:', error);
    }
  };

  // Add event listener for application updates
  useEffect(() => {
    const handleApplicationUpdate = () => {
      refreshAppliedJobs();
    };

    window.addEventListener('application-updated', handleApplicationUpdate);
    return () => {
      window.removeEventListener('application-updated', handleApplicationUpdate);
    };
  }, []);

  // Add state for loading more jobs in background
  const [loadingMoreJobs, setLoadingMoreJobs] = useState(false);

  // Listen for job matching preferences changes
  useEffect(() => {
    const handleJobMatchingPreferencesChanged = (event: Event) => {
      // Check if it's our custom event with forced refresh
      const isForceRefresh = event instanceof CustomEvent && 
        event.detail && 
        event.detail.forceRefresh === true;
      
      // More thoroughly clear the entire cache and state
      setJobCache(null);
      setOriginalMatchedJobs([]);
      setIsInitialLoad(true);
      
      // Also clear any in-progress operations
      processingKeys.clear();
      
      // Reset industry data to ensure fresh industry tags
      setAvailableCompanyIndustries([]);
      
      // Set loading state to show loading indicators
      setLoading(true);
      
      // Reset the search parameters (keeps the page but forces a re-fetch)
      const currentPage = searchParams.get('page') || '1';
      searchParams.set("page", "1"); // Force back to first page on preference changes
      setSearchParams(searchParams);
      
      // If it's a force refresh, explicitly call fetchAndFilterJobs after a brief delay
      if (isForceRefresh) {
        // Small delay to ensure component state is updated
        setTimeout(() => {
          fetchAndFilterJobs(true); // Force refresh
        }, 100);
      } else {
        // Still force refresh even without explicit force flag
        setTimeout(() => {
          fetchAndFilterJobs(true);
        }, 100);
      }
    };

    // Listen for both the normal event and a more specific modal event
    window.addEventListener('jobMatchingPreferencesChanged', handleJobMatchingPreferencesChanged);
    window.addEventListener('jobMatchingModalChanged', handleJobMatchingPreferencesChanged);
    
    return () => {
      window.removeEventListener('jobMatchingPreferencesChanged', handleJobMatchingPreferencesChanged);
      window.removeEventListener('jobMatchingModalChanged', handleJobMatchingPreferencesChanged);
    };
  }, [searchParams, setSearchParams, fetchAndFilterJobs, processingKeys]);

  // Update the prepareLocationOptions function to use our new filter system
  const prepareLocationOptions = (locationOptions: FilterOption[]) => {
    // Our new system already formats and prepares the options
    return locationOptions;
  };

  // Helper function to get filter display titles - simplified 
  const getFilterTitle = (type: string) => {
    if (type === 'locations') return 'Location';
    if (type === 'experienceLevels') return 'Work Environment';
    if (type === 'industries') return 'Industry';
    return type.charAt(0).toUpperCase() + type.slice(1, type.length - 1);
  };

  // Fix the dependencies for the filter change effect
  useEffect(() => {
    // Skip if we're actively changing pages
    if (pageStateRef.current.isChangingPage) {
      return;
    }
    
    // Skip this effect during the initial render
    if (isInitialLoad) return;
    
    // Only proceed if we have a filter index
    if (filterIndex && filterIndex.allJobs.length > 0) {
      // Set filtering locally state
      setIsFilteringLocally(true);
      
      // Filter the jobs using our optimized index
      const filteredJobs = filterJobsFromCache(filterIndex, {
        locations: selectedLocations,
        experienceLevels: selectedEnvironments,
        industries: selectedIndustries
      });

      // Calculate valid options with the new filters
      const { 
        locationOptions, 
        experienceLevelOptions, 
        industryOptions,
        updatedIndex
      } = calculateValidOptions(filterIndex, {
        locations: selectedLocations,
        experienceLevels: selectedEnvironments,
        industries: selectedIndustries
      });
      
      // Update filter options
      setAvailableLocationOptions(locationOptions);
      setAvailableExperienceLevelOptions(experienceLevelOptions);
      setAvailableIndustryOptions(industryOptions);
      
      // Update filter index
      setFilterIndex(updatedIndex);
      
      // CRITICAL: Reset page to 1 when filters change
      const oldPage = pageStateRef.current.currentPage;
      pageStateRef.current.currentPage = 1;
      pageStateRef.current.isChangingPage = true;
      
      // Keep the URL clean. Reuse the guarded helper that skips updates
      // when the pathname already contains a "/job/" segment.
      safeReplacePageParam(1);
      
      // Get the jobs for page 1
      const startIndex = 0;
      const endIndex = itemsPerPage;
      const pageJobs = filteredJobs.slice(startIndex, endIndex);
      
      // Update job results
      setJobResults(pageJobs);
      setOriginalMatchedJobs(filteredJobs);
      setCurrentPageData({
        page: 1,
        jobs: pageJobs
      });
      
      // Update pagination
      const totalPages = Math.ceil(filteredJobs.length / itemsPerPage);
      setTotalResults(filteredJobs.length);
      setTotalPages(totalPages);
      pageStateRef.current.totalPages = totalPages;
      
      setIsFilteringLocally(false);
      
      // Reset URL ignore flag after a delay
      setTimeout(() => {
        pageStateRef.current.isChangingPage = false;
      }, 100);
    } else if (!isFilteringLocally && !loading) {
      // If we don't have a filter index, fetch jobs from API
      fetchAndFilterJobs(false);
    }
  }, [
    selectedLocations, 
    selectedEnvironments, 
    selectedIndustries, 
    filterIndex, 
    isInitialLoad, 
    isFilteringLocally, 
    loading, 
    filterJobsFromCache, 
    calculateValidOptions,
    itemsPerPage,
    fetchAndFilterJobs
  ]);

  // Initialize the page state ref
  useEffect(() => {
    // Get initial page from URL
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    
    // Set our internal page state from URL, but only on initial mount
    pageStateRef.current = {
      currentPage: urlPage,
      isChangingPage: false,
      totalPages: 1
    };
  }, []);

  // Simple handlePageClick that will work reliably
  const handlePageClick = useCallback((data: { selected: number }) => {
    const newPage = data.selected + 1;
    const currentPage = pageStateRef.current.currentPage;
    
    // Skip if already on the same page
    if (newPage === currentPage) {
      return;
    }
    
    // Set flags to prevent filter logic from running
    pageStateRef.current.isChangingPage = true;
    pageStateRef.current.currentPage = newPage;
    
    try {
      // Get filtered jobs if we have a filter index
      if (filterIndex && filterIndex.allJobs.length > 0) {
        // Get filtered jobs
        const filteredJobs = filterJobsFromCache(filterIndex, {
          locations: selectedLocations,
          experienceLevels: selectedEnvironments,
          industries: selectedIndustries
        });
        
        // Get jobs for the new page
        const startIndex = (newPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const pageJobs = filteredJobs.slice(startIndex, endIndex);
        
        // Update job results
        setJobResults(pageJobs);
        setOriginalMatchedJobs(filteredJobs);
        
        // Update pagination state
        setCurrentPageData({
          page: newPage,
          jobs: pageJobs
        });
        
        // IMPORTANT: Basic URL update with replaceState
        safeReplacePageParam(newPage);
        
        // Update other pagination info
        setTotalResults(filteredJobs.length);
        setTotalPages(Math.ceil(filteredJobs.length / itemsPerPage));
        
        // Scroll to top
        window.scrollTo({ top: 0, behavior: "auto" });
      } else {
        // Need to fetch data
        setLoading(true);
        fetchAndFilterJobs(true);
      }
    } catch (error) {
      console.error("Error during pagination:", error);
      setError("Failed to load jobs for this page. Please try again.");
      setLoading(false);
    } finally {
      // Reset page change flag after a delay
      setTimeout(() => {
        pageStateRef.current.isChangingPage = false;
      }, 50);
    }
  }, [
    selectedLocations,
    selectedEnvironments,
    selectedIndustries,
    itemsPerPage,
    fetchAndFilterJobs,
    filterJobsFromCache,
    filterIndex,
    setError
  ]);

  // Simple function to ensure URL is in sync, but only when needed
  const ensureUrlInSync = () => {
    // Skip if we're in the middle of a page change operation
    if (pageStateRef.current.isChangingPage) {
      return;
    }
    
    // No need to constantly check - this is just a safety measure
    // This will only run once per render
  };

  // The render function
  return (
    <div className="job-search-container">
      {/* Call ensureUrlInSync but don't try to render its return value */}
      {(() => {
        ensureUrlInSync();
        return null;
      })()}
      <Sidebar />
      <div className="job-search">
        <div className={`filter-container ${scrolled ? "scrolled" : ""}`}>
          <div className={`filter-content ${loading || isFilteringLocally ? "filters-loading" : ""}`}>
            <button 
              className="mobile-menu-button" 
              onClick={() => {
                const sidebar = document.querySelector('.sidebar');
                sidebar?.classList.toggle('expanded');
              }}
            >
              <FiMenu />
            </button>
            
            <button 
              className="filters-button" 
              onClick={handleOpenFilterModal}
              disabled={loading || isFilteringLocally}
            >
              <AiOutlineFilter />
              Filters
            </button>
            
            <FilterDropdown
              options={availableLocationOptions}
              selectedValues={selectedLocations}
              setSelectedValues={setSelectedLocations}
              title="Location"
              type="locations"
              loading={loading || isFilteringLocally}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <FilterDropdown
              options={availableExperienceLevelOptions}
              selectedValues={selectedEnvironments}
              setSelectedValues={setSelectedEnvironments}
              title="Work Environment"
              type="experienceLevels"
              loading={loading || isFilteringLocally}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <FilterDropdown
              options={availableIndustryOptions}
              selectedValues={selectedIndustries}
              setSelectedValues={setSelectedIndustries}
              title="Industry"
              type="industries"
              loading={loading || isFilteringLocally}
              activeDropdown={activeDropdown}
              setActiveDropdown={setActiveDropdown}
            />
            <button 
              className={`clear-filters-button ${(loading || isFilteringLocally) ? "disabled" : ""} ${
                selectedLocations.length > 0 || 
                selectedEnvironments.length > 0 || 
                selectedIndustries.length > 0 ? "has-filters" : ""
              }`} 
              onClick={(loading || isFilteringLocally) ? undefined : clearFilters}
              disabled={loading || isFilteringLocally}
            >
              {isClearing ? 'Clearing...' : 'Clear Filters'}
            </button>
            
            {loadingMoreJobs && (
              <div className="loading-more-indicator">
                Finding more matching jobs...
              </div>
            )}
          </div>
        </div>

        <div className={`job-results job-tiles-container ${currentJobs.length <= 3 && currentJobs.length > 0 ? 'few-tiles' : ''}`}>
          {loading ? (
            <>
              {[...Array(itemsPerPage)].map((_, index) => (
                <div key={index} className="job-tile skeleton" />
              ))}
            </>
          ) : isFilteringLocally ? (
            <>
              {[...Array(Math.min(3, itemsPerPage))].map((_, index) => (
                <div key={index} className="job-tile skeleton" />
              ))}
            </>
          ) : currentJobs.length > 0 ? (
            currentJobs.map((job, index) => (
              <JobTile
                key={`${job._id}-${index}`}
                job={job}
                userId={user?._id}
                handleSelection={handleSelection}
                context={JobButtonContext.MAIN}
                isApplied={appliedJobs.has(job._id)}
                onApplicationUpdate={refreshAppliedJobs}
              />
            ))
          ) : (
            <p>No jobs found matching the selected filters.</p>
          )}
        </div>

        {totalResults > itemsPerPage && (
          <div className="pagination-container">
            <ReactPaginate
              previousLabel={<FaAngleLeft />}
              nextLabel={<FaAngleRight />}
              pageCount={totalPages}
              onPageChange={handlePageClick}
              forcePage={pageStateRef.current.currentPage - 1}
              containerClassName="pagination"
              activeClassName="active"
              previousClassName="prev"
              nextClassName="next"
              disabledClassName="disabled"
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              breakLabel="..."
              breakClassName="break"
              breakLinkClassName="break-link"
              pageLinkClassName="page-link"
              renderOnZeroPageCount={null}
              key={`paginate-${totalPages}-${pageStateRef.current.currentPage}`}
            />
          </div>
        )}
      </div>

      {isFilterModalOpen && (
        <div
          className={`filter-modal-overlay ${isFilterModalOpen ? "modal-open" : ""} ${
            isModalClosing ? "closing" : ""
          }`}
          onClick={handleCloseFilterModal}
        >
          <div className="filter-modal" onClick={(e) => e.stopPropagation()}>
            <button
              className="modal-close-button"
              onClick={handleCloseFilterModal}
              aria-label="Close modal"
            >
              <AiOutlineClose />
            </button>
            <h2>Filters</h2>
            
            <div className="filter-section">
              <span className="filter-label">{getFilterTitle('locations')}</span>
              <FilterDropdown
                options={availableLocationOptions}
                selectedValues={tempSelectedLocations}
                setSelectedValues={setTempSelectedLocations}
                title={getFilterTitle('locations')}
                type="locations"
                loading={loading || isFilteringLocally}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
              />
            </div>

            <div className="filter-section">
              <span className="filter-label">{getFilterTitle('experienceLevels')}</span>
              <FilterDropdown
                options={availableExperienceLevelOptions}
                selectedValues={tempSelectedEnvironments}
                setSelectedValues={setTempSelectedEnvironments}
                title={getFilterTitle('experienceLevels')}
                type="experienceLevels"
                loading={loading || isFilteringLocally}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
              />
            </div>

            <div className="filter-section">
              <span className="filter-label">{getFilterTitle('industries')}</span>
              <FilterDropdown
                options={availableIndustryOptions}
                selectedValues={tempSelectedIndustries}
                setSelectedValues={setTempSelectedIndustries}
                title={getFilterTitle('industries')}
                type="industries"
                loading={loading || isFilteringLocally}
                activeDropdown={activeDropdown}
                setActiveDropdown={setActiveDropdown}
              />
            </div>

            <div className="modal-actions">
              <button className="cancel-button" onClick={handleCloseFilterModal}>
                Cancel
              </button>
              <button className="apply-button" onClick={handleApplyFilters}>
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {selection && (
        <JobDetailsModal
          job={selection}
          isOpen={Boolean(selection._id)}
          onClose={() => setSelection(null)}
        />
      )}
    </div>
  );
};

export default JobSearch;
